/**
 * Terminal partial-failure panel.
 *
 * Two CTAs:
 *  - Retry: replays the stored input on the server (no body sent).
 *  - Edit & resubmit: clears the form lock so the user can change values
 *    and POST a fresh enrollment with a new idempotency key.
 *
 * The 429 retry-blocked variant swaps the buttons for cleanup messaging.
 */

import { AlertTriangle, RefreshCw, Edit3, ShieldX } from "lucide-react"
import {
  ENROLLMENT_STEP_LABELS,
  EnrollmentResponse,
  EnrollmentStepName
} from "../types"

interface PartialProps {
  enrollment: EnrollmentResponse
  isRetrying?: boolean
  onRetry: () => void
  onEditAndResubmit: () => void
}

interface BlockedProps {
  enrollmentId: number
  message: string
}

const formatDetails = (
  details: Record<string, unknown> | null
): string | null => {
  if (!details) return null
  const entries = Object.entries(details).filter(
    ([k]) => k !== "field" && k !== "value"
  )
  if (entries.length === 0) return null
  return entries
    .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join(" · ")
}

export const EnrollmentPartial = ({
  enrollment,
  isRetrying,
  onRetry,
  onEditAndResubmit
}: PartialProps) => {
  const failedStepName: EnrollmentStepName | undefined =
    enrollment.failed_step ??
    (Object.entries(enrollment.steps).find(
      ([, s]) => s.status === "failed"
    )?.[0] as EnrollmentStepName | undefined)

  const failedStep = failedStepName ? enrollment.steps[failedStepName] : null
  const detailsLine = failedStep ? formatDetails(failedStep.details) : null

  return (
    <div className="overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-sm">
      <header className="px-6 py-5 bg-gradient-to-br from-amber-50 to-white border-b border-amber-100">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white">
            <AlertTriangle size={18} />
          </div>
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-amber-700">
              Enrollment paused
            </div>
            <h2 className="mt-1 font-serif text-lg text-neutral-900 leading-tight">
              {failedStepName
                ? `Step "${ENROLLMENT_STEP_LABELS[failedStepName]}" failed`
                : "Something went wrong"}
            </h2>
          </div>
        </div>

        {failedStep?.error_message && (
          <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
            {failedStep.error_message}
          </p>
        )}
        {detailsLine && (
          <p className="mt-2 text-xs font-mono text-neutral-500">
            {detailsLine}
          </p>
        )}
      </header>

      <div className="px-6 py-5 space-y-3">
        <button
          type="button"
          onClick={onRetry}
          disabled={isRetrying}
          className="group w-full flex items-start gap-4 rounded-xl border border-neutral-200 bg-white p-4 text-left transition-all hover:border-primary-300 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500 group-hover:bg-primary-100">
            <RefreshCw size={18} className={isRetrying ? "animate-spin" : ""} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-neutral-900">
              {isRetrying ? "Retrying…" : "Retry"}
            </div>
            <p className="text-xs text-neutral-500 mt-0.5">
              Replays the same input on the server. Use this when the cause was
              transient (DB blip, OpenSearch hiccup) or you fixed the underlying
              state (e.g., deleted a duplicate user).
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={onEditAndResubmit}
          disabled={isRetrying}
          className="group w-full flex items-start gap-4 rounded-xl border border-neutral-200 bg-white p-4 text-left transition-all hover:border-primary-300 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500 group-hover:bg-primary-100">
            <Edit3 size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-neutral-900">
              Edit &amp; resubmit
            </div>
            <p className="text-xs text-neutral-500 mt-0.5">
              Unlocks the form so you can change values, then submits a fresh
              enrollment with a new idempotency key. The partial resources from
              this attempt stay in place — clean them up manually later.
            </p>
          </div>
        </button>

        <div className="pt-3 mt-2 border-t border-neutral-100 text-[11px] font-mono text-neutral-400">
          Enrollment #{enrollment.enrollment_id} · retry{" "}
          {enrollment.retry_count}/5
        </div>
      </div>
    </div>
  )
}

export const EnrollmentRetryBlocked = ({
  enrollmentId,
  message
}: BlockedProps) => (
  <div className="overflow-hidden rounded-2xl border border-red-200 bg-white shadow-sm">
    <header className="px-6 py-5 bg-gradient-to-br from-red-50 to-white border-b border-red-100">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-500 text-white">
          <ShieldX size={18} />
        </div>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-red-700">
            Retry limit reached
          </div>
          <h2 className="mt-1 font-serif text-lg text-neutral-900 leading-tight">
            Enrollment #{enrollmentId} can't be retried
          </h2>
        </div>
      </div>
      <p className="mt-3 text-sm text-neutral-700 leading-relaxed">{message}</p>
    </header>

    <div className="px-6 py-5">
      <p className="text-sm text-neutral-600 leading-relaxed">
        This enrollment has been retried 5 times. The orphan resources (org,
        brand, etc.) created during earlier attempts are still in place — clean
        them up manually before starting a fresh enrollment with a new
        idempotency key.
      </p>
      <div className="mt-4 rounded-lg bg-neutral-50 border border-neutral-100 px-4 py-3 text-xs font-mono text-neutral-600 leading-relaxed">
        Inspect:&nbsp;
        <span className="text-neutral-900">
          SELECT * FROM bb_org_enrollment WHERE id = {enrollmentId};
        </span>
      </div>
    </div>
  </div>
)

export default EnrollmentPartial
